import React from 'react';
import dayjs from 'dayjs';
import {
  Phone,
  Calendar,
  Trash2,
  ExternalLink,
  MoreHorizontal,
  Search,
  ArrowUpDown,
  Settings
} from 'lucide-react';
import { Spin, Pagination, Tooltip, DatePicker } from 'antd';
import CallActionDropdownV2 from './CallActionDropdownV2';

const CallListTableV2 = ({
  data,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onCall,
  onReschedule,
  onRemove,
  onMove,
  onActionClick,
  onSearch,
  onDateChange,
  customColumns = [],
  canAdd = true,
  canEdit = true,
  canDelete = true,
  canSearch = true,
  rolePermissions,
}) => {

  const rangePresets = [
    { label: 'Today', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
    { label: 'Yesterday', value: [dayjs().subtract(1, 'd').startOf('day'), dayjs().subtract(1, 'd').endOf('day')] },
    { label: 'This Week', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
    { label: 'Last 30 Days', value: [dayjs().subtract(30, 'd'), dayjs()] },
    { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
    { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  ];

  const ActionButton = ({ icon, color, label, onClick }) => (
    <Tooltip title={label}>
      <button
        onClick={onClick}
        className={`p-2 rounded-lg transition-all hover:shadow-md hover:scale-110 ${color} bg-white border border-slate-100 text-slate-500 hover:text-slate-900`}
      >
        {icon}
      </button>
    </Tooltip>
  );

  return (
    <div className="flex flex-col">
      {/* Table Toolbar */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-4 flex-1">
          {canSearch && (
            <div className="relative max-w-md w-full group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={18} />
              <input
                type="text"
                placeholder="Search by name, mobile..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
          )}
          <DatePicker.RangePicker
            className="h-[40px] rounded-xl border-slate-200 flex-none"
            onChange={onDateChange}
            placeholder={['From Date', 'To Date']}
            presets={rangePresets}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-500">Show:</span>
          <select
            className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-sm font-bold text-slate-700 focus:ring-2 focus:ring-blue-500/20 outline-none"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {[10, 20, 50, 100].map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto min-h-[400px] relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[1px] flex items-center justify-center">
            <Spin size="large" />
          </div>
        )}

        <table className="w-full text-left border-collapse min-w-[1000px]">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs font-bold uppercase tracking-wider border-b border-slate-100">
              <th className="px-6 py-4 sticky left-0 bg-slate-50 z-20 min-w-[200px]">
                <div className="flex items-center gap-2 cursor-pointer hover:text-slate-900 transition-colors group">
                  <span>Name</span>
                  <ArrowUpDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </th>
              <th className="px-6 py-4">Actions</th>
              <th className="px-6 py-4">Schedule Time</th>
              {customColumns.map((col, idx) => (
                <th key={idx} className="px-6 py-4">{col.DisplayName}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {data.length > 0 ? data.map((record, idx) => (
              <tr key={idx} className="hover:bg-blue-50/30 transition-colors group">
                <td className="px-6 py-4 sticky left-0 bg-white group-hover:bg-blue-50/30 z-10">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs shrink-0 ${record.EntityName?.toLowerCase() === 'lead'
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                      : 'bg-rose-500 text-white shadow-lg shadow-rose-200'
                      }`}>
                      {record.EntityName?.toLowerCase() === 'lead' ? 'L' : 'E'}
                    </div>
                    <div className="truncate max-w-[150px]">
                      <div className="font-bold text-slate-900 truncate">{record.PersonName || record.ScheduleMobileNo}</div>
                      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{record.EntityName}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {canAdd && (
                      <ActionButton icon={<Phone size={16} className="text-emerald-500 fill-emerald-50" />} label="Call Now" onClick={() => onCall(record)} />
                    )}
                    {canEdit && (
                      <ActionButton icon={<Calendar size={16} className="text-blue-500" />} label="Reschedule" onClick={() => onReschedule(record)} />
                    )}
                    {canDelete && (
                      <ActionButton icon={<Trash2 size={16} className="text-rose-500" />} label="Remove" onClick={() => onRemove(record)} />
                    )}
                    {canEdit && (
                      <ActionButton icon={<ExternalLink size={16} className="text-slate-500" />} label="Move" onClick={() => onMove(record)} />
                    )}
                    {canEdit && (
                      <CallActionDropdownV2
                        record={record}
                        onAction={onActionClick}
                        permissions={record.AllowedActions || []}
                        rolePermissions={rolePermissions}
                      />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-700">
                      {record.ScheduleTime && !record.ScheduleTime.includes('1900') ? record.ScheduleTime.split(' ')[0] : 'N/A'}
                    </span>
                    <span className="text-xs text-slate-500 font-medium">
                      {record.ScheduleTime && !record.ScheduleTime.includes('1900') ? record.ScheduleTime.split(' ')[1] : ''}
                    </span>
                  </div>
                </td>
                {customColumns.map((col, colIdx) => (
                  <td key={colIdx} className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-600 truncate block max-w-[150px]">
                      {record[col.ColumnName] || '-'}
                    </span>
                  </td>
                ))}
              </tr>
            )) : (
              <tr>
                <td colSpan={3 + customColumns.length} className="px-6 py-20 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-slate-50 p-4 rounded-full">
                      <Search size={40} className="text-slate-300" />
                    </div>
                    <div className="text-slate-500 font-bold">No records found</div>
                    <p className="text-slate-400 text-sm">Try adjusting your filters or search term</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-500">
          Showing <span className="text-slate-900">{(currentPage - 1) * pageSize + 1}</span> to <span className="text-slate-900">{Math.min(currentPage * pageSize, totalCount)}</span> of <span className="text-slate-900">{totalCount}</span> entries
        </div>
        <Pagination
          current={currentPage}
          total={totalCount}
          pageSize={pageSize}
          onChange={onPageChange}
          showSizeChanger={false}
          className="custom-pagination"
        />
      </div>
    </div>
  );
};

export default CallListTableV2;
